import { useMemo } from 'react'
import ReactApexChart from 'react-apexcharts'
import { ApexOptions } from 'apexcharts'
import { useAiFilters } from './AiFilterContext'
import { ACCURACY_KPIS, buildAccuracyHistoryForSite, getSiteWape } from './mockData'
import { SiteDef, sitesForCompany } from '../profiles/dashboard/catalog'
import { ChartFrame, baseChart, tightGrid } from '../profiles/dashboard/ChartFrame'

const AiAccuracyView = () => {
  const { company, site, horizon, siteDef } = useAiFilters()
  const history = useMemo(
    () => buildAccuracyHistoryForSite(site.value, siteDef),
    [site.value, siteDef]
  )
  const siteWape = getSiteWape(site.label, siteDef)
  const companySiteWape = useMemo(
    () =>
      sitesForCompany(company.value).map((s: SiteDef) => {
        const w = getSiteWape(s.name, s)!
        return { ...w, site: s.name, type: s.type }
      }),
    [company.value]
  )
  const windowHistory = useMemo(
    () => history.slice(-Math.min(30, horizon.value + 10)),
    [history, horizon.value]
  )

  const lineOptions: ApexOptions = useMemo(
    () => ({
      chart: { ...baseChart, type: 'line', toolbar: { show: false } },
      grid: tightGrid,
      stroke: { curve: 'smooth', width: 2.5 },
      dataLabels: { enabled: false },
      xaxis: {
        categories: windowHistory.map((r) => r.date.slice(5)),
        title: { text: 'Date' },
        labels: { hideOverlappingLabels: true, trim: true },
      },
      yaxis: {
        title: { text: 'Daily electricity (kWh)' },
        labels: {
          formatter: (v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : `${Math.round(v)}`),
        },
      },
      colors: ['#3E97FF', '#50CD89'],
      legend: { position: 'top', horizontalAlign: 'left', floating: false },
      tooltip: {
        shared: true,
        y: { formatter: (v) => `${Math.round(v).toLocaleString()} kWh` },
      },
    }),
    [windowHistory]
  )

  const lineSeries = useMemo(
    () => [
      { name: 'Predicted', data: windowHistory.map((r) => r.predicted) },
      { name: 'Actual', data: windowHistory.map((r) => r.actual) },
    ],
    [windowHistory]
  )

  const apeOptions: ApexOptions = useMemo(
    () => ({
      chart: { ...baseChart, type: 'bar', toolbar: { show: false } },
      grid: tightGrid,
      plotOptions: { bar: { columnWidth: '55%', borderRadius: 2 } },
      dataLabels: { enabled: false },
      xaxis: {
        categories: windowHistory.map((r) => r.date.slice(5)),
        title: { text: 'Date' },
        labels: { hideOverlappingLabels: true, trim: true },
      },
      yaxis: { title: { text: 'Absolute % error' }, max: 40 },
      colors: ['#FFC700'],
      legend: { show: false },
      tooltip: { y: { formatter: (v) => `${v}%` } },
    }),
    [windowHistory]
  )

  const apeSeries = useMemo(
    () => [{ name: 'APE', data: windowHistory.map((r) => r.ape) }],
    [windowHistory]
  )

  return (
    <>
      <div className='row g-4 mb-5'>
        {[
          { label: 'Rolling 7d WAPE', value: `${ACCURACY_KPIS.rolling7Wape}%`, tone: 'primary' },
          { label: 'Rolling 30d WAPE', value: `${ACCURACY_KPIS.rolling30Wape}%`, tone: 'info' },
          { label: 'Match-day WAPE', value: `${ACCURACY_KPIS.matchDayWape}%`, tone: 'danger' },
          { label: 'Non-match WAPE', value: `${ACCURACY_KPIS.nonMatchWape}%`, tone: 'success' },
          {
            label: 'Site WAPE (h=7)',
            value: siteWape ? `${siteWape.wapeH7}%` : '—',
            tone: 'warning',
          },
          {
            label: 'Drift',
            value: ACCURACY_KPIS.driftStatus,
            tone: 'dark',
          },
        ].map((k) => (
          <div className='col-6 col-md-4 col-xl-2' key={k.label}>
            <div className='card border-0 shadow-sm h-100'>
              <div className='card-body'>
                <div className='fs-7 text-gray-500 fw-semibold'>{k.label}</div>
                <div className={`fs-2 fw-bold text-${k.tone} text-capitalize`}>{k.value}</div>
                <div className='fs-8 text-muted'>{site.label}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className='row g-4 mb-5'>
        <div className='col-lg-7' style={{ minWidth: 0 }}>
          <div className='card border-0 shadow-sm h-100 overflow-hidden'>
            <div className='card-header border-0 pt-6'>
              <h3 className='card-title fw-bold fs-4'>Predicted vs actual · {site.label}</h3>
            </div>
            <div className='card-body pt-0' style={{ minWidth: 0 }}>
              <ChartFrame>
                <ReactApexChart
                  options={lineOptions}
                  series={lineSeries}
                  type='line'
                  width='100%'
                  height={320}
                />
              </ChartFrame>
            </div>
          </div>
        </div>
        <div className='col-lg-5' style={{ minWidth: 0 }}>
          <div className='card border-0 shadow-sm h-100 overflow-hidden'>
            <div className='card-header border-0 pt-6'>
              <h3 className='card-title fw-bold fs-4'>Daily APE</h3>
            </div>
            <div className='card-body pt-0' style={{ minWidth: 0 }}>
              <ChartFrame>
                <ReactApexChart
                  options={apeOptions}
                  series={apeSeries}
                  type='bar'
                  width='100%'
                  height={320}
                />
              </ChartFrame>
            </div>
          </div>
        </div>
      </div>

      <div className='card border-0 shadow-sm mb-5'>
        <div className='card-header border-0 pt-6'>
          <h3 className='card-title fw-bold fs-4'>Site WAPE</h3>
        </div>
        <div className='card-body pt-0'>
          <div className='table-responsive'>
            <table className='table table-row-dashed align-middle gs-0 gy-3 mb-0'>
              <thead>
                <tr className='fw-semibold text-muted fs-7 text-uppercase'>
                  <th>Site</th>
                  <th>Type</th>
                  <th>WAPE h=1</th>
                  <th>WAPE h=7</th>
                  <th>WAPE h=30</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {companySiteWape.map((row) => (
                  <tr
                    key={row.site}
                    className={row.site === site.label ? 'bg-light-primary' : undefined}
                  >
                    <td className='fw-semibold'>{row.site}</td>
                    <td>
                      <span className='badge badge-light text-capitalize'>
                        {row.type.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td>{row.wapeH1}%</td>
                    <td>{row.wapeH7}%</td>
                    <td>{row.wapeH30}%</td>
                    <td className='text-muted fs-7'>{row.note || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className='card border-0 shadow-sm'>
        <div className='card-header border-0 pt-6'>
          <h3 className='card-title fw-bold fs-4'>Anomaly inbox</h3>
          <div className='card-toolbar'>
            <span className='badge badge-light-warning'>
              {history.filter((r) => r.anomaly).length} open
            </span>
          </div>
        </div>
        <div className='card-body pt-0'>
          {history
            .filter((r) => r.anomaly)
            .map((r) => (
              <div
                key={r.date}
                className='d-flex flex-wrap align-items-center justify-content-between gap-3 border rounded-3 p-4 mb-3'
              >
                <div>
                  <div className='fw-bold'>
                    {r.date} · {site.label}
                  </div>
                  <div className='text-muted fs-7'>
                    Predicted {r.predicted.toLocaleString()} kWh · Actual{' '}
                    {r.actual.toLocaleString()} kWh · APE {r.ape}%
                  </div>
                </div>
                <div className='d-flex gap-2'>
                  <button type='button' className='btn btn-sm btn-light'>
                    Dismiss
                  </button>
                  <button type='button' className='btn btn-sm btn-primary'>
                    Create alarm
                  </button>
                </div>
              </div>
            ))}
        </div>
      </div>
    </>
  )
}

export default AiAccuracyView
